import { Stack, useTheme } from "@mui/material";
import { Flag, MapPin } from "lucide-react";
import { useClientQuery } from "web_myplann/Api/Client/useClientQuery";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { ProfileAddressWithTypeId } from "web_myplann/Profile/Types/ProfileAddressWithTypeId";
import { getProfileAddresses } from "web_myplann/Profile/Utils/getProfileAddresses";
import { DataRowCard } from "web_myplann/Shared/Components/DataRowCard";
import { EmptyListSkeleton } from "web_myplann/Shared/Components/EmptyListSkeleton";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";

export const AddressesPage = () => {
	const theme = useTheme();
	const { t } = useResource();

	const { data: userData } = useClientQuery();

	const addresses = userData?.addresses as
		| ProfileAddressWithTypeId[]
		| undefined;
	const { permanentAddress, contactAddresses } = getProfileAddresses(addresses);

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.Addresses.title)}
					defaultRoute={Routing.Dashboard.path()}
				/>
			}
		>
			<Stack p={1.6}>
				{!permanentAddress &&
				(!contactAddresses || contactAddresses.length === 0) ? (
					<EmptyListSkeleton
						label={t(Resources.Profile.EmptyList.Addresses.label)}
						description={t(Resources.Profile.EmptyList.Addresses.description)}
					/>
				) : (
					<>
						<DataRowCard
							icon={
								<Flag
									size={theme.iconSize.sm}
									color={theme.palette.deprecated.grey[700]}
								/>
							}
							label={t(Resources.Addresses.PermanentAddress.title)}
							noDivider={contactAddresses?.length === 0}
							items={[
								{
									label: t(Resources.Addresses.PermanentAddress.Street),
									value: permanentAddress?.street,
								},
								{
									label: t(Resources.Addresses.PermanentAddress.StreetNumber),
									value: permanentAddress?.descriptiveNumber,
								},
								{
									label: t(Resources.Addresses.PermanentAddress.City),
									value: permanentAddress?.city,
								},
								{
									label: t(Resources.Addresses.PermanentAddress.Zip),
									value: permanentAddress?.zip,
								},
								{
									label: t(Resources.Addresses.PermanentAddress.Country),
									value: permanentAddress?.country,
								},
							]}
						/>

						{contactAddresses?.map((address, addressIndex) => (
							<DataRowCard
								key={`${address.street}-${address.city}`}
								icon={
									<MapPin
										size={theme.iconSize.sm}
										color={theme.palette.deprecated.grey[700]}
									/>
								}
								label={t(Resources.Addresses.ContactAddress.title)}
								noDivider={addressIndex === contactAddresses.length - 1}
								items={[
									{
										label: t(Resources.Addresses.ContactAddress.Street),
										value: address.street,
									},
									{
										label: t(Resources.Addresses.ContactAddress.StreetNumber),
										value: address.descriptiveNumber,
									},
									{
										label: t(Resources.Addresses.ContactAddress.City),
										value: address.city,
									},
									{
										label: t(Resources.Addresses.ContactAddress.Zip),
										value: address.zip,
									},
									{
										label: t(Resources.Addresses.ContactAddress.Country),
										value: address.country,
									},
								]}
							/>
						))}
					</>
				)}
			</Stack>
		</MyPlannLayout>
	);
};
